import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconContext } from 'react-icons'
import { Container, NavList, NavItem, NavLabel, NavIcon } from './styles'
import { useUserStore } from '../../store/user'
import { useDirectory } from '../../store/directory'
import {
  FaHouse,
  FaFile,
  FaCheckDouble,
  FaFilePen,
  FaUser,
  FaUsers,
  FaChartArea,
  FaChartPie,
  FaCode,
  FaBoxArchive,
  FaRobot,
  FaScaleBalanced,
  FaChevronDown,
} from 'react-icons/fa6'

export interface RouteItem {
  label: string
  to?: string
  Icon: any
  adminOnly?: boolean
  allowedRoles?: string[]
  children?: RouteItem[]
}

const routes: RouteItem[] = [
  {
    label: 'Início',
    to: '/',
    Icon: FaHouse,
    adminOnly: true,
  },
  {
    label: 'Gerenciador de Documentos',
    to: '/documents',
    Icon: FaFile,
  },
  {
    label: 'Conferência (SIM)',
    to: '/conferencia',
    Icon: FaCheckDouble,
  },
  {
    label: 'Tipos de Documentos',
    to: '/documentTypes',
    Icon: FaFilePen,
    adminOnly: true,
  },
  {
    label: 'Usuários e Grupos',
    Icon: FaUsers,
    allowedRoles: ['admin', 'manager'],
    children: [
      {
        label: 'Gerenciador de Usuários',
        to: '/users',
        Icon: FaUser,
        allowedRoles: ['admin', 'manager'],
      },
      {
        label: 'Gerenciador de Grupos',
        to: '/groups',
        Icon: FaUsers,
        adminOnly: true,
      },
    ],
  },
  {
    label: 'Relatórios',
    Icon: FaChartPie,
    adminOnly: true,
    children: [
      {
        label: 'Relatórios e Estatísticas',
        to: '/reports',
        Icon: FaChartPie,
        adminOnly: true,
      },
      {
        label: 'Logs',
        to: '/logs',
        Icon: FaChartArea,
        adminOnly: true,
      },
    ],
  },
  {
    label: 'Integração com API',
    to: '/api-integration',
    Icon: FaCode,
    adminOnly: true,
  },
  {
    label: 'Arquivo Físico',
    to: '/physical-archive',
    Icon: FaBoxArchive,
  },
  {
    label: 'Avaliação & TTD (CPAD)',
    to: '/cpad',
    Icon: FaScaleBalanced,
    allowedRoles: ['admin', 'manager'],
  },
  {
    label: 'Inteligência Artificial',
    to: '/ai-settings',
    Icon: FaRobot,
    adminOnly: true,
  },
]

interface DropdownProps {
  items: RouteItem[]
  position: { top: number; left: number }
  onClose: () => void
  onNavigate: (to: string) => void
  isActive: (to: string) => boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
}

const DropdownPortal: React.FC<DropdownProps> = ({
  items,
  position,
  onClose,
  onNavigate,
  isActive,
  onMouseEnter,
  onMouseLeave,
}) => {
  return createPortal(
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        minWidth: 220,
        backgroundColor: '#20248c',
        borderRadius: 8,
        padding: '6px',
        boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -4px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {items.map((child) => {
        const active = child.to ? isActive(child.to) : false
        const ChildIcon = child.Icon

        return (
          <button
            key={child.to || child.label}
            type="button"
            onClick={() => {
              if (child.to) {
                onNavigate(child.to)
                onClose()
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 6,
              background: active ? 'rgba(255, 255, 255, 0.22)' : 'transparent',
              border: active ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
              color: active ? '#ffffff' : '#e2e8f0',
              fontWeight: active ? 700 : 500,
              fontSize: 13,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (!active) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.14)'
                e.currentTarget.style.color = '#ffffff'
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = '#e2e8f0'
              }
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChildIcon size={14} color={active ? '#ffffff' : '#cbd5e1'} />
            </span>
            <span style={{ letterSpacing: '-0.01em' }}>{child.label}</span>
          </button>
        )
      })}
    </div>,
    document.body
  )
}

const TopNav: React.FC = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user } = useUserStore()

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const handleOpenDropdown = (label: string, element: HTMLElement) => {
    clearTimer()
    const rect = element.getBoundingClientRect()
    setDropdownPos({ top: rect.bottom + 4, left: rect.left })
    setActiveDropdown(label)
  }

  const handleCloseDropdown = () => {
    clearTimer()
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null)
      setDropdownPos(null)
    }, 150)
  }

  useEffect(() => {
    const handleScrollOrResize = () => {
      clearTimer()
      setActiveDropdown(null)
      setDropdownPos(null)
    }

    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
      clearTimer()
    }
  }, [])

  const isRouteActive = (to?: string) => {
    if (!to) return false
    if (pathname === '/' && to === '/') return true
    if (to === '/') return false
    return pathname.startsWith(to)
  }

  const isParentActive = (item: RouteItem) => {
    if (item.to && isRouteActive(item.to)) return true
    if (item.children) {
      return item.children.some((child) => isRouteActive(child.to))
    }
    return false
  }

  const handleNavigate = (to: string) => {
    if (to === '/documents') {
      useDirectory.getState().setDirectory([
        {
          name: 'Início',
          url: '/',
        },
      ])
    }
    navigate(to)
  }

  const isItemVisible = (item: RouteItem) => {
    if (!user) return false
    if (user.role === 'admin') return true
    if (item.adminOnly) return false
    if (item.allowedRoles && item.allowedRoles.length > 0) {
      return item.allowedRoles.includes(user.role) || (user as any)?.manager
    }
    return true
  }

  const visibleRoutes = routes
    .map((item) => {
      if (item.children) {
        const visibleChildren = item.children.filter(isItemVisible)
        if (visibleChildren.length === 0) return null
        return {
          ...item,
          children: visibleChildren,
        }
      }
      return isItemVisible(item) ? item : null
    })
    .filter(Boolean) as RouteItem[]

  const currentOpenItem = visibleRoutes.find((r) => r.label === activeDropdown)

  return (
    <IconContext.Provider value={{ color: '#cbd5e1' }}>
      <Container>
        <NavList>
          {visibleRoutes.map((item) => {
            const hasChildren = !!item.children && item.children.length > 0
            const active = isParentActive(item)
            const isOpen = activeDropdown === item.label
            const Icon = item.Icon
            const iconColor = active || isOpen ? '#ffffff' : '#cbd5e1'

            if (hasChildren) {
              return (
                <NavItem
                  key={item.label}
                  active={active || isOpen}
                  onClick={(e) => {
                    if (isOpen) {
                      setActiveDropdown(null)
                      setDropdownPos(null)
                    } else {
                      handleOpenDropdown(item.label, e.currentTarget)
                    }
                  }}
                  onMouseEnter={(e) => handleOpenDropdown(item.label, e.currentTarget)}
                  onMouseLeave={handleCloseDropdown}
                  title={item.label}
                >
                  <NavIcon>
                    <Icon size={14} color={iconColor} />
                  </NavIcon>
                  <NavLabel>{item.label}</NavLabel>
                  <FaChevronDown
                    size={9}
                    color={iconColor}
                    style={{
                      marginLeft: 2,
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                </NavItem>
              )
            }

            return (
              <NavItem
                key={item.to}
                active={active}
                onClick={() => item.to && handleNavigate(item.to)}
                title={item.label}
              >
                <NavIcon>
                  <Icon size={14} color={active ? '#ffffff' : '#cbd5e1'} />
                </NavIcon>
                <NavLabel>{item.label}</NavLabel>
              </NavItem>
            )
          })}
        </NavList>

        {currentOpenItem && currentOpenItem.children && dropdownPos && (
          <DropdownPortal
            items={currentOpenItem.children}
            position={dropdownPos}
            onClose={() => {
              setActiveDropdown(null)
              setDropdownPos(null)
            }}
            onNavigate={handleNavigate}
            isActive={isRouteActive}
            onMouseEnter={clearTimer}
            onMouseLeave={handleCloseDropdown}
          />
        )}
      </Container>
    </IconContext.Provider>
  )
}

export default TopNav
